import { heldRefusalContext, maskTelemetryText, telemetryBasename, telemetryCode } from '../janus-engine/telemetry/refusal-beacon';
import { createStallWatchdog, runStallWorker, type StallContext } from './stall-watchdog';
import './stall-mark';

type StallWindow = Window & {
    encounter_arr?: unknown; caster_arr?: unknown; ftModifyEncounterPageEncounterArr?: unknown;
    ftGetActiveEncounterId?: () => string; ftGetActiveCasterId?: () => string;
    FateTide?: { encounterCache?: Map<string, unknown>; casterCache?: Map<string, unknown> };
};

/** The existing refusal reporter owns the endpoint and installs this before the gameplay modules. */
export function installStallWatchdog(endpoint: string, build: string | null): void {
    const page = telemetryBasename(location.pathname);
    if (!['my_campaign.php', 'ft3d_scene_host.php'].includes(page) || typeof Worker === 'undefined') return;
    const host = window as StallWindow;
    if (host.ftStallMark) return;
    const url = URL.createObjectURL(new Blob([`(${runStallWorker.toString()})(${createStallWatchdog.toString()},${JSON.stringify(new URL(endpoint, location.href).href)})`],
        { type: 'text/javascript' }));
    let worker: Worker;
    try { worker = new Worker(url, { name: 'ft-main-thread-stall' }); }
    catch (error) { URL.revokeObjectURL(url); throw error; }
    let label = 'page:boot', scenePhase = '', stopped = false;
    const uuid = (value: unknown): string | null => typeof value === 'string'
        && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value) ? value.toUpperCase() : null;
    const metric = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) && value >= 0
        && value <= 99_999_999_999 ? value : null;
    const ping = (visible = document.visibilityState !== 'hidden'): void => {
        if (stopped) return;
        try {
            const encounterId = String(host.ftGetActiveEncounterId?.() ?? ''), actorId = String(host.ftGetActiveCasterId?.() ?? '');
            const cached = (cache: Map<string, unknown> | undefined, id: string): unknown => cache?.get(id) ?? cache?.get(id.toUpperCase());
            const context = heldRefusalContext(cached(host.FateTide?.encounterCache, encounterId)
                ?? host.encounter_arr ?? host.ftModifyEncounterPageEncounterArr, cached(host.FateTide?.casterCache, actorId) ?? host.caster_arr);
            const memory = (performance as Performance & { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number } }).memory;
            const value: StallContext = { ...context, encounter_id: context.encounter_id ?? uuid(encounterId)
                ?? uuid(new URL(location.href).searchParams.get('encounter_id')), page, surface: 'ft3d', build, label,
                memory: [metric(memory?.usedJSHeapSize), metric(memory?.totalJSHeapSize), metric(memory?.jsHeapSizeLimit),
                    metric((navigator as Navigator & { deviceMemory?: number }).deviceMemory), metric(navigator.hardwareConcurrency)] };
            worker.postMessage({ context: value, at: performance.timeOrigin + performance.now(), visible });
        } catch { /* Missing context or browser APIs must never affect play. */ }
    };
    host.ftStallMark = value => {
        try {
            // Keep existing codes intact; mask identities before bounding human stage labels.
            const code = telemetryCode(value) ?? telemetryCode(maskTelemetryText(value, value.length)
                .replace(/[^a-z0-9_.:-]+/gi, '-').replace(/-+/g, '-').slice(0, 64).replace(/^-+|-+$/g, ''));
            if (code) { label = code; ping(); }
        } catch { /* Observer only. */ }
    };
    worker.onmessage = ({ data }) => { if (data === 'ready') { URL.revokeObjectURL(url); host.ftStallWatchdogReady = true; } };
    const timer = setInterval(() => {
        // sceneload23 owns preview3d's phase writes. Read its existing presentation label.
        const phase = document.querySelector('[data-ft-zone-loading-phase]')?.textContent ?? '';
        if (phase && phase !== scenePhase) {
            scenePhase = phase;
            label = 'scene:' + phase.replace(/[^a-z0-9_.:-]+/gi, '-').slice(0, 58);
        }
        ping();
    }, 1000);
    const stop = (): void => { stopped = true; clearInterval(timer); worker.terminate(); URL.revokeObjectURL(url); host.ftStallWatchdogReady = false; };
    worker.onerror = stop;
    document.addEventListener('visibilitychange', () => ping());
    window.addEventListener('pagehide', event => { if (event.persisted) ping(false); else stop(); });
    window.addEventListener('pageshow', () => ping());
    ping();
}
